/**
 * @render react
 * @name TaxonomyFilter
 * @example
 * <TaxonomyFilter label="Show me:" content={[{label: 'Content 1', value: '/content1'}, {label: 'Content 2', value: '/content2'}]} />
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { Icon } from 'Components/atoms/Icon';
import { Select } from 'Components/atoms/Select';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * TaxonomyFilter component
 * @param {string} label - Label of the component
 * @param {array} content - Array containing the links to display. Keys must be label and value, which will be used mainly by the Select component
 * @param {string} currentPageUrl - Optional string containing the URL of the current page. If not provided, will try to get the current path name
 * @param {string} mode - Mode to be used. Valid values can be select or list
 * @param {Object} listModeOptions - Object containing options for list mode
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} context - Extra information coming from Polaris provider.
 */
class TaxonomyFilter extends PureComponent {
  /**
   * Constructor
   * @param props
   */
  constructor(props) {
    super(props);

    // Initial state. By default, the panel is collapsed
    this.state = {
      isOpen: false,
      showToggle: false,
    };

    // Set the ref for the container to query the children to get the width
    this.setContainerRef = (element) => {
      this.containerRef = element;
    };

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);

    this.checkToggleVisibilityEvent = null;
  }

  /**
   * Once component is mounted
   */
  componentDidMount() {
    const { mode } = this.props;

    if (typeof window !== 'undefined' && mode === 'list') {
      this.checkToggleVisibility();

      // Upon each resize, check if we show the show all button
      this.checkToggleVisibilityEvent = this.debounce(this.checkToggleVisibility, 500);
      window.addEventListener('resize', this.checkToggleVisibilityEvent);
    }
  }

  componentDidUpdate() {
    const { mode } = this.props;

    if (typeof window !== 'undefined' && mode === 'list') {
      this.checkToggleVisibility();
    }
  }

  componentWillUnmount() {
    const { mode } = this.props;

    if (typeof window !== 'undefined' && mode === 'list') {
      window.removeEventListener('resize', this.checkToggleVisibilityEvent);
    }
  }

  /**
   * When the user selects an option
   */
  /* eslint-disable-next-line class-methods-use-this */
  onSelectChange(e) {
    const { value } = e;
    window.location.href = value;
  }

  /**
   * When we click the see more/less button
   */
  onToggleClick() {
    const { isOpen } = this.state;

    this.setState({
      isOpen: !isOpen,
    });
  }

  /**
   * Checks whether we show the toggle button
   */
  checkToggleVisibility() {
    const {
      context: { cssPrefix },
    } = this.props;
    let showToggle = false;

    if (this.containerRef) {
      const el = this.containerRef.querySelector(`.${cssPrefix}__taxonomy-filter--list`);
      // Calculate the available width
      const availableWidth = el.offsetWidth;
      // Get all the items within that div
      const listItems = el.querySelectorAll('li');
      const listItemsLength = listItems.length;
      let i = 0;
      let totalWidth = 0;

      // Loop through the items
      for (i = 0; i < listItemsLength; i += 1) {
        // Get the width and the margins
        const itemStyle = listItems[i].currentStyle || window.getComputedStyle(listItems[i]);
        const itemWidth = parseInt(itemStyle.width, 10) + parseInt(itemStyle.marginRight, 10);
        totalWidth += itemWidth;

        // Check if we can fit the item
        if (totalWidth > availableWidth) {
          // if not, we show the show toggle button
          showToggle = true;
          break;
        }
      }

      this.setState({
        showToggle,
      });
    }
  }

  /**
   * Debounce method
   * @param {*} callback
   * @param {*} wait
   */
  debounce(callback, wait) {
    let timeout;
    return (...args) => {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => callback.apply(context, args), wait);
    };
  }

  /**
   * Render function
   */
  render() {
    const {
      label,
      content,
      currentPageUrl,
      mode,
      listModeOptions,
      loadStyles,
      extraClassNames,
      context: { cssPrefix },
    } = this.props;
    const {
      isOpen,
      showToggle,
    } = this.state;

    const pathname = typeof window !== 'undefined' && window.location.pathname;
    const currentPathName = currentPageUrl || pathname;

    // get the selected option from the currentPageUrl, because react-select needs the complete object for the defaultValue, not just the value
    const selectedOption = content.filter(option => option.value === currentPathName);
    const seeOptionLabel = isOpen ? listModeOptions.seeLessLabel : listModeOptions.seeMoreLabel;
    return (
      <div
        className={classNames({
          [`${cssPrefix}__taxonomy-filter`]: loadStyles,
          [`-mode-${mode}`]: true,
          '-collapsed': (mode === 'list' && !isOpen),
          '-expanded': (mode === 'list' && isOpen),
          ...extraClassNames,
        })}
        ref={this.setContainerRef}
      >
        {label && (
          <span className={`${cssPrefix}__taxonomy-filter--label`}>{label}</span>
        )}

        {mode === 'select'
          ? (
            <div className={`${cssPrefix}__taxonomy-filter--dropdown`}>
              <Select
                id={`${cssPrefix}__taxonomy-filter--taxonomy`}
                name="taxonomy"
                options={content}
                onChange={this.onSelectChange}
                selectedOption={selectedOption}
                isClearable={false}
                extraClassNames={{
                  [`${cssPrefix}__taxonomy-filter--select`]: true,
                }}
                reactSelectProps={{
                  'aria-label': label,
                }}
              />
            </div>
          ) : (
            <>
              <div className={`${cssPrefix}__taxonomy-filter--list`}>
                <ul>
                  {content.map((item, index) => (
                    <li
                      key={index}
                      className={classNames({
                        '-current': (currentPathName === item.value),
                      })}
                    >
                      <Link href={item.value}>{item.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              {showToggle && (
                <div className={`${cssPrefix}__taxonomy-filter--toggle`}>
                  <Button
                    onClick={this.onToggleClick}
                    srLabel={(`${seeOptionLabel} ${label || 'Filters'}`).toLowerCase()}
                  >
                    {seeOptionLabel}
                    {listModeOptions.icon ? <Icon icon={`${cssPrefix}__ico-${listModeOptions.icon}`} /> : ''}
                  </Button>
                </div>
              )}
            </>
          )}
      </div>
    );
  }
}

/**
 * Valid Props
 */
TaxonomyFilter.propTypes = {
  label: PropTypes.string,
  content: PropTypes.arrayOf(PropTypes.shape()),
  currentPageUrl: PropTypes.string,
  mode: PropTypes.string,
  listModeOptions: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
};

/**
 * Default props
 */
TaxonomyFilter.defaultProps = {
  label: null,
  content: [],
  currentPageUrl: null,
  mode: 'list',
  listModeOptions: {
    seeMoreLabel: 'See more',
    seeLessLabel: 'See less',
    icon: null,
  },
  loadStyles: true,
  extraClassNames: {},
  context: {},
};

export default compose(withPolaris)(TaxonomyFilter);
